import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import { ArrowRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { IconPicker, renderIconByName } from "@/components/communication/IconPicker";
import { loadBoards, saveBoard, CommunicationBoardData } from "@/lib/communicationBoardTypes";
import { useTranslation } from "@/hooks/useTranslation";

const boardEmojis = ["📋", "💬", "🏠", "❤️", "🎮", "📚", "🍽️", "🎵", "⭐", "🌈"];

export default function BoardDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const boardId = searchParams.get("id");
  const { t, dir } = useTranslation();

  const [board, setBoard] = useState<CommunicationBoardData | null>(null);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (boardId) {
      const boards = loadBoards();
      const existing = boards.find((b) => b.id === boardId);
      if (existing) {
        setBoard(existing);
      } else {
        // New board - create default
        setBoard({
          id: boardId,
          name: t("newBoard"),
          icon: "MessageCircle",
          rows: 2,
          columns: 3,
          cells: Array(2).fill(null).map(() => Array(3).fill(null)),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
    }
  }, [boardId, t]);

  const handleConfirm = () => {
    if (board) {
      saveBoard(board);
    }
    navigate(`/communication/builder?edit=${boardId}`);
  };

  if (!board) {
    return (
      <MainLayout>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">{t("loading")}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex-1 flex flex-col bg-background p-6" dir={dir}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <Button
            variant="ghost"
            size="lg"
            onClick={handleConfirm}
            data-scan-item
            className="p-3 rounded-2xl"
          >
            <ArrowRight className="w-8 h-8" />
          </Button>
          <h1 className="text-3xl font-bold text-foreground">{t("boardDetails")}</h1>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="max-w-lg mx-auto w-full space-y-6"
        >
          <div className="bg-card rounded-3xl border-2 border-border p-8 space-y-6">
            {/* Name Input */}
            <div className="space-y-2">
              <Label htmlFor="boardName">{t("boardName")}</Label>
              <Input
                ref={nameInputRef}
                id="boardName"
                value={board.name}
                onChange={(e) => setBoard({ ...board, name: e.target.value })}
                placeholder={t("enterBoardName")}
                data-scan-item
                className="h-14 text-lg"
              />
            </div>

            {/* Icon Selection */}
            <div className="space-y-2">
              <Label>{t("icon")}</Label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowIconPicker(!showIconPicker)}
                  data-scan-item
                  className={cn(
                    "w-16 h-16 rounded-xl border-2 flex items-center justify-center transition-all",
                    "hover:border-primary hover:bg-primary/10",
                    board.icon ? "border-primary bg-primary/10" : "border-border"
                  )}
                >
                  {board.icon ? (
                    renderIconByName(board.icon, "w-8 h-8 text-primary")
                  ) : (
                    <Plus className="w-7 h-7 text-muted-foreground" />
                  )}
                </button>
                {board.icon && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setBoard({ ...board, icon: undefined })}
                  >
                    {t("remove")}
                  </Button>
                )}
              </div>
              {showIconPicker && (
                <div className="p-4 rounded-xl border bg-card mt-2">
                  <IconPicker
                    selectedIcon={board.icon}
                    onSelect={(i) => {
                      setBoard({ ...board, icon: i, emoji: undefined });
                      setShowIconPicker(false);
                    }}
                  />
                </div>
              )}
            </div>

            {/* Emoji Selection */}
            <div className="space-y-2">
              <Label>{t("orChooseEmoji")}</Label>
              <div className="flex flex-wrap gap-3">
                {boardEmojis.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => setBoard({ ...board, emoji, icon: undefined })}
                      data-scan-item
                      className={cn(
                        "w-14 h-14 text-2xl rounded-lg border-2 transition-all",
                        "hover:border-primary hover:bg-primary/10",
                        board.emoji === emoji ? "border-primary bg-primary/10" : "border-border"
                      )}
                    >
                      {emoji}
                    </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="p-6 rounded-xl border-2 border-dashed border-primary/30 bg-primary/5">
              <p className="text-xs text-muted-foreground mb-3">{t("preview")}:</p>
              <div className="flex items-center justify-center gap-4">
                {board.icon && renderIconByName(board.icon, "w-10 h-10 text-primary")}
                {board.emoji && <span className="text-4xl">{board.emoji}</span>}
                <span className="text-xl font-bold">{board.name}</span>
              </div>
            </div>

            {/* Confirm Button */}
            <Button
              onClick={handleConfirm}
              size="lg"
              data-scan-item
              className="w-full h-16 text-lg rounded-2xl"
            >
              {t("confirm")}
            </Button>
          </div>
        </motion.div>
      </div>
    </MainLayout>
  );
}
